import { FC } from "react";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPlanningsApi } from "../../http/carPlannings/api";
import { CarPlanning } from "../../http/carPlannings/types";
import { formatDateNumeric } from "../../types/utils";
import HistoryDialog from "../ui/HistoryDialog";
import VersionDialog from "../ui/VersionDialog";

type CarPlanningHistoryDialogProps = {
  itemToShowVersions: CarPlanning;
  onClose: () => void;
};

const CarPlanningHistoryDialog: FC<CarPlanningHistoryDialogProps> = ({ itemToShowVersions, onClose }) => {
  const i18n = useI18n();
  const carPlanningsApi = useCarPlanningsApi();

  return (
    <HistoryDialog
      title={i18n.t.carPlannings.versions}
      lastVersion={itemToShowVersions.versionNumber}
      entityId={itemToShowVersions.id}
      getEntityVersionHook={carPlanningsApi.getAdminCarPlanningVersion}
      getHistory={carPlanningsApi.getAdminCarPlanningHistory}
      onClose={onClose}
      renderForm={(headers, dataVersion) => (
        <>
          <VersionDialog.Table headers={headers}>
            {dataVersion.current?.year != dataVersion.next.year && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.year}
                  value={dataVersion.current?.year}
                  edited={dataVersion.next.year}
                />
              </tr>
            )}

            {dataVersion.current?.nr != dataVersion.next.nr && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.number}
                  value={dataVersion.current?.nr}
                  edited={dataVersion.next.nr}
                />
              </tr>
            )}

            {dataVersion.current?.driverName != dataVersion.next.driverName && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.driverName}
                  value={dataVersion.current?.driverName}
                  edited={dataVersion.next.driverName}
                />
              </tr>
            )}

            {dataVersion.current?.managerName != dataVersion.next.managerName && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.managerName}
                  value={dataVersion.current?.managerName}
                  edited={dataVersion.next.managerName}
                />
              </tr>
            )}

            {dataVersion.current?.costCenter != dataVersion.next.costCenter && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.costCenter}
                  value={dataVersion.current?.costCenter}
                  edited={dataVersion.next.costCenter}
                />
              </tr>
            )}

            {dataVersion.current?.startDate != dataVersion.next.startDate && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.startDate}
                  value={formatDateNumeric(dataVersion.current?.startDate, i18n.language)}
                  edited={formatDateNumeric(dataVersion.next?.startDate, i18n.language)}
                />
              </tr>
            )}

            {(dataVersion.current?.subsidiary?.id ?? null) !== (dataVersion.next.subsidiary?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.subsidiaries.singular}
                  value={dataVersion.current && dataVersion.current?.subsidiary?.name}
                  edited={dataVersion.next.subsidiary?.name}
                />
              </tr>
            )}

            {(dataVersion.current?.carArrivalDate ?? null) !== (dataVersion.next.carArrivalDate ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.plannedArrivalDate}
                  value={
                    dataVersion.current?.carArrivalDate &&
                    formatDateNumeric(dataVersion.current?.carArrivalDate, i18n.language)
                  }
                  edited={
                    dataVersion.next?.carArrivalDate &&
                    formatDateNumeric(dataVersion.next?.carArrivalDate, i18n.language)
                  }
                />
              </tr>
            )}

            {dataVersion.current?.status != dataVersion.next.status && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.state}
                  value={dataVersion.current?.status}
                  edited={dataVersion.next.status}
                />
              </tr>
            )}

            {dataVersion.current?.priceWithoutVat != dataVersion.next.priceWithoutVat && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.priceWithoutVat}
                  value={dataVersion.current?.priceWithoutVat}
                  edited={dataVersion.next.priceWithoutVat}
                />
              </tr>
            )}
          </VersionDialog.Table>
        </>
      )}
    />
  );
};

export default CarPlanningHistoryDialog;
